// eslint-disable-next-line
import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Stack,
  Link,
  Container,
  AppBar,
  Toolbar,
  Divider,
  Card,
  CardContent,
  CardMedia,
  useMediaQuery,
  useTheme,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import PageTransition from '../components/PageTransition';
import { styled, Theme, keyframes } from '@mui/material/styles';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Menu as MenuIcon,
  LocalShipping as LocalShippingIcon,
  Speed as SpeedIcon,
  Public as PublicIcon,
  Inventory as InventoryIcon,
  Apartment as ApartmentIcon,
  EventAvailable as EventAvailableIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Apple as AppleIcon,
  Android as AndroidIcon
} from '@mui/icons-material';
import React from 'react';

// Import fonts
// Oswald font is loaded via Google Fonts in embedded code
// Poppins font is loaded via Google Fonts in embedded code

// Define colors
const DARK_BG = '#0A0A0A';
const DARKER_BG = '#050505';
const WHITE_TEXT = '#FFFFFF';
const RED_COLOR = '#DE1F27';
const PINK_RED = '#FF2992';

// Add TextFade component
const TextFade = ({
  direction = 'down',
  children,
  staggerChildren = 0.1,
}: {
  direction?: 'up' | 'down';
  children: React.ReactNode;
  staggerChildren?: number;
}) => {
  const FADE_ANIMATION = {
    show: { opacity: 1, y: 0, transition: { type: 'spring', bounce: 0.3 } },
    hidden: { opacity: 0, y: direction === 'down' ? -18 : 18 },
  };
  
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'show' : ''}
      variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: staggerChildren,
          },
        },
      }}
      style={{ width: '100%', textAlign: 'center' }}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? (
          <motion.div variants={FADE_ANIMATION}>{child}</motion.div>
        ) : (
          child
        )
      )}
    </motion.div>
  );
};

const GradientSpan = styled('span')(({ theme }) => ({
  background: 'linear-gradient(90deg,#DE1F27 0%,#DE1F27 50%,#DE1F27 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  fontWeight: 600,
  fontFamily: '"Oswald", sans-serif',
  letterSpacing: '1px',
  textTransform: 'uppercase'
}));

// Animation keyframes
const float = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 0.6; }
  100% { opacity: 0.4; }
`;

// Animation keyframes for traffic lanes
const moveTraffic = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// Transportation route effect
const RouteMap = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '200%',
  height: '100%',
  background: `
    linear-gradient(90deg, transparent 0%, transparent 49%, ${RED_COLOR}40 50%, transparent 51%, transparent 100%),
    linear-gradient(0deg, transparent 0%, transparent 49%, ${RED_COLOR}40 50%, transparent 51%, transparent 100%)
  `,
  backgroundSize: '80px 80px',
  opacity: 0.45,
  zIndex: 0,
  animation: `${float} 35s linear infinite`,
  pointerEvents: 'none',
}));

const HighwayPattern = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '200%',
  height: '100%',
  background: `
    repeating-linear-gradient(90deg, 
      transparent, 
      transparent 100px, 
      ${RED_COLOR}60 100px, 
      ${RED_COLOR}60 120px, 
      transparent 120px, 
      transparent 200px
    )
  `,
  opacity: 0.35,
  zIndex: 0,
  animation: `${float} 20s linear infinite, ${pulse} 4s ease-in-out infinite`,
  pointerEvents: 'none',
}));

// Mouse follower gradient effect
const GradientLight = styled('div')({
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${RED_COLOR}30 0%, ${PINK_RED}15 40%, transparent 70%)`,
  filter: 'blur(60px)',
  opacity: 0.8,
  pointerEvents: 'none',
  transition: 'transform 0.15s ease-out, width 0.2s ease, height 0.2s ease',
  transform: 'translate(-50%, -50%)',
  zIndex: 0,
});

// Styled components
const PageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  backgroundColor: DARKER_BG,
  [theme.breakpoints.down('md')]: {
    minHeight: '-webkit-fill-available'
  }
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: DARK_BG,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  color: WHITE_TEXT,
  overflowY: 'auto',
  position: 'relative',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'url("/motex-transport-vehicle8.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '55vh',
  overflow: 'hidden',
  zIndex: 10,
  width: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    zIndex: 1
  },
  [theme.breakpoints.down('md')]: {
    minHeight: '40vh',
  }
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.5)',
  border: '1px solid rgba(255,255,255,0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 20px rgba(0,0,0,0.4)',
    '& .service-title': {
      color: RED_COLOR,
    }
  },
}));

const TrafficLanes = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '25%',
  left: 0,
  width: '200%',
  height: '50%',
  background: `
    repeating-linear-gradient(90deg, 
      transparent,
      transparent 30px, 
      ${RED_COLOR}80 30px, 
      ${RED_COLOR}80 60px
    )
  `,
  backgroundSize: '90px 8px',
  backgroundRepeat: 'repeat-x',
  opacity: 0.7,
  zIndex: 0,
  animation: `${moveTraffic} 10s linear infinite`,
  pointerEvents: 'none',
}));

// Carousel components
const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  touchAction: 'pan-y',
}));

const CarouselTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  transition: 'transform 0.5s ease-in-out',
  width: '100%',
}));

const ServicesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logisticsMenuAnchor, setLogisticsMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleLogisticsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLogisticsMenuAnchor(event.currentTarget);
  };

  const handleLogisticsMenuClose = () => {
    setLogisticsMenuAnchor(null);
  };

  const handleServiceClick = (serviceMode: string) => {
    handleLogisticsMenuClose();
    navigate('/instant-quote', { state: { selectedService: serviceMode } });
    window.scrollTo(0, 0);
  };
  
  // Function to handle app download
  const handleAppDownload = (platform: 'apple' | 'android') => {
    window.open('https://pwa-final-101.vercel.app/', '_blank');
    setIsMobileMenuOpen(false);
    // Close the app menu after clicking a download option
    const appMenu = document.getElementById('app-download-menu');
    if (appMenu) {
      appMenu.style.display = 'none';
    }
  };
  
  // Function to close the app download menu when clicking outside
  const closeAppMenu = (e: MouseEvent) => {
    const appMenu = document.getElementById('app-download-menu');
    const installButton = document.getElementById('install-app-button');
    
    // Close only if clicking outside both the menu and button
    if (appMenu && 
        e.target instanceof Node && 
        !appMenu.contains(e.target) && 
        installButton && 
        !installButton.contains(e.target)) {
      appMenu.style.display = 'none';
      document.removeEventListener('click', closeAppMenu);
    }
  };
  
  // Mouse position state
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isMoving, setIsMoving] = useState(false);
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const movingTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Update mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Track mouse position relative to the viewport
      setMousePos({ 
        x: e.clientX, 
        y: e.clientY 
      });
      
      // Set moving state
      setIsMoving(true);
      
      // Clear previous timeout
      if (movingTimeout.current) {
        clearTimeout(movingTimeout.current);
      }
      
      // Set timeout to track when movement stops
      movingTimeout.current = setTimeout(() => {
        setIsMoving(false);
      }, 150);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (movingTimeout.current) {
        clearTimeout(movingTimeout.current);
      }
    };
  }, []);
  
  // Function to handle navigation with scroll to top
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };
  
  // This function is already defined above
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setIsMobileMenuOpen(true);
  };
  
  const handleMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    // Set loaded after a short delay to trigger animations
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Service data
  const services = [
    {
      id: 1,
      title: 'Car Hire',
      description: 'Professional car hire services with modern, well-maintained vehicles. Perfect for business trips, special occasions, or when you need reliable transportation.',
      image: '/car-hire-1.png'
    },
    {
      id: 2,
      title: 'Parcel Delivery',
      description: 'Fast and secure parcel delivery solutions for businesses and individuals. We ensure your packages arrive safely and on schedule.',
      image: '/services-15.jpg'
    },
    {
      id: 3,
      title: 'Chauffeur Services',
      description: 'Professional chauffeur services with experienced drivers. We provide reliable transportation for executives, special events, and VIP clients.',
      image: '/chauffeur-2.jpg'
    },
    {
      id: 4,
      title: 'Fragile Freight',
      description: 'Specialized handling for delicate and valuable items. Our experts use proper techniques and materials to protect your fragile shipments.',
      image: '/gallery 6.jpeg'
    },
    {
      id: 5,
      title: 'Door to Door Service',
      description: 'Convenient pickup and delivery directly from your location to the destination. Let us handle the logistics while you focus on your business.',
      image: '/gallery 2.jpg'
    },
    {
      id: 6,
      title: 'Same Day Delivery',
      description: 'Urgent deliveries handled with speed and reliability. Our same-day service ensures your time-sensitive packages reach their destination promptly.',
      image: '/gallery 3.jpg'
    },
    {
      id: 7,
      title: 'Interstate Delivery',
      description: 'Seamless interstate logistics solutions connecting businesses across Australia. Our fleet ensures safe and timely delivery across state lines.',
      image: '/upscalemedia-transformed.jpeg'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <PageTransition>
      <PageWrapper>
      {/* Header - Updated to match landing page */}
      <AppBar position="fixed" color="transparent" elevation={0} sx={{ py: 1, backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(8px)', zIndex: 1100 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Logo on the left */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: isMobile ? '50%' : '20%' }}>
            <Box 
              component="button"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate('/');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer' 
              }}
            >
              <Box 
                component="img" 
                src="/MOTEX+Logo.png" 
                alt="MOTEX Logo" 
                sx={{ height: 28 }} 
              />
            </Box>
          </Box>
          
          {/* Desktop menu in the center */}
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '60%' }}>
              <Stack 
                direction="row" 
                spacing={2.5} 
                sx={{ 
                  color: 'white', 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400,
                  justifyContent: 'center',
                  fontSize: '15px',
                  lineHeight: '29px',
                }}
              >
                <Link 
                  component="button" 
                  onClick={() => handleNavigation('/')}
                  color="inherit" 
                  underline="none" 
                  sx={{ 
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Home
                </Link>
                <Link
                  component="button"
                  onClick={() => handleServiceClick('Car Hire')}
                  color="inherit"
                  underline="none"
                  sx={{
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Car Hire
                </Link>
                <Link
                  component="button"
                  onClick={() => handleServiceClick('Chauffeur')}
                  color="inherit"
                  underline="none"
                  sx={{
                    '&:hover': { color: RED_COLOR },
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 400
                  }}
                >
                  Chauffeur
                </Link>
                <Box
                  onMouseEnter={(e) => handleLogisticsMenuOpen(e)}
                  sx={{ display: 'inline-block' }}
                >
                  <Link
                    component="button"
                    color="inherit"
                    underline="none"
                    sx={{
                      '&:hover': { color: RED_COLOR },
                      fontFamily: '"Poppins", sans-serif',
                      fontSize: '16px',
                      lineHeight: '29px',
                      fontWeight: 400
                    }}
                  >
                    Logistics Services
                  </Link>
                  <Menu
                    anchorEl={logisticsMenuAnchor}
                    open={Boolean(logisticsMenuAnchor)}
                    onClose={handleLogisticsMenuClose}
                    PaperProps={{
                      onMouseLeave: handleLogisticsMenuClose,
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          fontSize: '14px',
                          fontFamily: '"Poppins", sans-serif',
                          transition: 'color 0.2s ease',
                          '&:hover': {
                            color: RED_COLOR,
                            backgroundColor: 'rgba(222, 31, 39, 0.08)'
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem onClick={() => handleServiceClick('Car Hire')}>Car Hire</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Parcel Delivery')}>Parcel Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Fragile Freight')}>Fragile Freight</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Door to Door Service')}>Door to Door Service</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Same Day Delivery')}>Same Day Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Interstate Delivery')}>Interstate Delivery</MenuItem>
                  </Menu>
                </Box>
                <Link 
                  component="button"
                  onClick={() => handleNavigation('/services')}
                  sx={{ 
                    color: RED_COLOR,
                    textDecoration: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '16px',
                    lineHeight: '29px',
                    fontWeight: 600,
                    '&:hover': { color: RED_COLOR }
                  }}
                >
                  Services
                </Link>
                <Link 
                  component="button"
                  onClick={() => handleNavigation('/about-us')}
                  color="inherit" 
                  underline="none" 
                  sx={{ '&:hover': { color: RED_COLOR } }}
                >
                  About Us
                </Link>
                <Link 
                  component="button"
                  onClick={() => handleNavigation('/instant-quote')}
                  color="inherit" 
                  underline="none" 
                  sx={{ '&:hover': { color: RED_COLOR } }}
                >
                  Instant Quote
                </Link>
                <Link 
                  component="button"
                  onClick={() => handleNavigation('/gallery')}
                  color="inherit" 
                  underline="none" 
                  sx={{ '&:hover': { color: RED_COLOR } }}
                >
                  Gallery
                </Link>
                <Link 
                  component="button"
                  onClick={() => handleNavigation('/contact-us')}
                  color="inherit" 
                  underline="none" 
                  sx={{ '&:hover': { color: RED_COLOR } }}
                >
                  Contact
                </Link>
              </Stack>
            </Box>
          )}
          
          {/* Get A Quote button on the right */}
          <Box sx={{ display: 'flex', width: isMobile ? '50%' : '20%', justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* Mobile Car Hire, Chauffeur and Logistics buttons */}
            {isMobile && (
              <>
                <Button
                  component="button"
                  onClick={() => handleServiceClick('Car Hire')}
                  sx={{
                    color: 'white',
                    fontSize: '0.8rem',
                    mr: 1,
                    px: 1,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': { color: RED_COLOR },
                    whiteSpace: 'nowrap'
                  }}
                >
                  Car Hire
                </Button>
                <Button
                  component="button"
                  onClick={() => handleServiceClick('Chauffeur')}
                  sx={{
                    color: 'white',
                    fontSize: '0.8rem',
                    mr: 1,
                    px: 1,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    '&:hover': { color: RED_COLOR },
                    whiteSpace: 'nowrap'
                  }}
                >
                  Chauffeur
                </Button>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    component="button"
                    onClick={(e) => handleLogisticsMenuOpen(e)}
                    sx={{
                      color: 'white',
                      fontSize: '0.8rem',
                      mr: 1.5,
                      px: 1,
                      py: 0.5,
                      minWidth: 'auto',
                      textTransform: 'none',
                      fontFamily: '"Poppins", sans-serif',
                      '&:hover': { color: RED_COLOR },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Logistics
                  </Button>
                  <Menu
                    anchorEl={logisticsMenuAnchor}
                    open={Boolean(logisticsMenuAnchor)}
                    onClose={handleLogisticsMenuClose}
                    PaperProps={{
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          fontSize: '14px',
                          fontFamily: '"Poppins", sans-serif',
                          transition: 'color 0.2s ease',
                          '&:hover': {
                            color: RED_COLOR,
                            backgroundColor: 'rgba(222, 31, 39, 0.08)'
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem onClick={() => handleServiceClick('Car Hire')}>Car Hire</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Parcel Delivery')}>Parcel Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Fragile Freight')}>Fragile Freight</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Door to Door Service')}>Door to Door Service</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Same Day Delivery')}>Same Day Delivery</MenuItem>
                    <MenuItem onClick={() => handleServiceClick('Interstate Delivery')}>Interstate Delivery</MenuItem>
                  </Menu>
                </Box>
              </>
            )}
            
            {!isMobile && (
              <Button 
                component="button" 
                onClick={() => handleNavigation('/instant-quote')} 
                variant="contained" 
                sx={{ 
                  bgcolor: RED_COLOR, 
                  color: 'white',
                  textTransform: 'none',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  fontSize: '15px',
                  borderRadius: '50px',
                  px: 3,
                  py: 1,
                  minWidth: '130px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    bgcolor: '#c41922',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)'
                  }
                }}
              >
                Get&nbsp;a&nbsp;Quote
              </Button>
            )}
            
            {/* Mobile menu icon */}
            {isMobile && (
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={() => setIsMobileMenuOpen(true)}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Toolbar spacer to prevent content from being hidden under fixed AppBar */}
      <Box sx={{ height: '64px' }} />
      
      <ContentSection ref={contentSectionRef}>
        {/* Mouse follower gradient light - positioned relative to viewport */}
        <GradientLight 
          sx={{ 
            left: mousePos.x,
            top: mousePos.y,
            width: isMoving ? '600px' : '400px',
            height: isMoving ? '600px' : '400px',
            opacity: isMoving ? 0.85 : 0.7,
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 1
          }} 
        />
        
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center' 
              }}
            >
              <Typography 
                variant="h2" 
                align="center" 
                sx={{ 
                  mb: 1,
                  fontWeight: 800,
                  color: RED_COLOR,
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem', lg: '6rem' },
                  letterSpacing: { xs: '1px', sm: '2px', md: '3px' }
                }}
              >
                <TextFade direction="down" staggerChildren={0.03}>
                  <div style={{ whiteSpace: 'nowrap' }}>
                    OUR SERVICES
                  </div>
                </TextFade>
              </Typography>
            </motion.div>
          </Container>
        </HeroSection>

        {/* Content wrapper */}
        <Box sx={{ position: 'relative' }}>
          {/* Services Section */}
          <Box 
            sx={{ py: { xs: 5, md: 8 }, position: 'relative', overflow: 'hidden' }}
          >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate={isLoaded ? "visible" : "hidden"}
              >
                <Typography 
                  variant="h2" 
                  align="center" 
                  sx={{ 
                    mb: { xs: 1, md: 2 },
                    fontWeight: 700,
                    color: 'white',
                    fontFamily: '"Oswald", sans-serif',
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' }
                  }}
                >
                  WHAT WE OFFER
                </Typography>
                <Typography 
                  variant="body1" 
                  align="center" 
                  sx={{ 
                    mb: { xs: 4, md: 6 },
                    color: 'rgba(255,255,255,0.7)',
                    maxWidth: '700px',
                    mx: 'auto',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '16px' },
                    lineHeight: { xs: 1.5, md: '29px' },
                    fontWeight: 400
                  }}
                >
                  From local deliveries to interstate transport, our comprehensive range of services is designed to meet all your logistics needs with efficiency and reliability.
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                  {services.map((service) => (
                    <Grid item xs={6} sm={6} md={4} key={service.id}>
                      <motion.div variants={itemVariants}>
                        <ServiceCard onClick={() => {
                          navigate('/instant-quote', { state: { selectedService: service.title } });
                          window.scrollTo(0, 0);
                        }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={service.image}
                            alt={service.title}
                            sx={{ 
                              opacity: 0.8,
                              height: { xs: 100, sm: 140, md: 200 }
                            }}
                          />
                          <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                            <Typography 
                              variant="h5" 
                              className="service-title"
                              sx={{ 
                                mb: { xs: 0.75, sm: 1, md: 2 },
                                fontWeight: 600,
                                color: 'white',
                                fontFamily: '"Poppins", sans-serif',
                                fontSize: { xs: '0.85rem', sm: '1rem', md: '1.5rem' }
                              }}
                            >
                              {service.title}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'rgba(255,255,255,0.7)',
                                fontFamily: '"Poppins", sans-serif',
                                lineHeight: { xs: 1.4, sm: 1.6, md: '29px' },
                                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '16px' },
                                fontWeight: 400,
                                display: { xs: 'none', sm: 'block' } // Hide description on extra small screens
                              }}
                            >
                              {service.description}
                            </Typography>
                          </CardContent>
                        </ServiceCard>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Container>
          </Box>

          {/* CTA Section */}
          <Container maxWidth="lg" sx={{ py: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Box 
                sx={{ 
                  bgcolor: 'rgba(222, 31, 39, 0.1)', 
                  p: { xs: 3, md: 6 },
                  borderRadius: { xs: 3, md: 4 },
                  textAlign: 'center',
                  border: '1px solid rgba(222, 31, 39, 0.2)'
                }}
              >
                <Typography 
                  variant="h3" 
                  sx={{ 
                    mb: { xs: 1, md: 2 },
                    fontWeight: 700,
                    color: 'white',
                    fontFamily: '"Oswald", sans-serif',
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }
                  }}
                >
                  Ready to Get Started?
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: { xs: 3, md: 4 },
                    color: 'rgba(255,255,255,0.8)',
                    maxWidth: '700px',
                    mx: 'auto',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '16px' },
                    lineHeight: { xs: 1.5, md: '29px' },
                    fontWeight: 400
                  }}
                >
                  Contact us today to discuss your logistics needs and get a customized solution for your business.
                </Typography>
                <Button 
                  onClick={() => handleNavigation('/instant-quote')}
                  variant="contained" 
                  size="large"
                  sx={{ 
                    bgcolor: RED_COLOR, 
                    color: 'white',
                    textTransform: 'none',
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 400,
                    fontSize: '15px',
                    borderRadius: '50px',
                    px: 3,
                    py: 1,
                    minWidth: '130px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      bgcolor: '#c41922',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)'
                    }
                  }}
                >
                  Get&nbsp;a&nbsp;Quote
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ bgcolor: RED_COLOR, py: 6 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box 
                    component="img" 
                    src="/MOTEX+Logo.png" 
                    alt="MOTEX Logo" 
                    sx={{ 
                      height: 40, 
                      filter: 'brightness(0) invert(1)'
                    }} 
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, mb: 3, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                  MOTEX Transport is a leading provider of logistics and transportation services across Australia, offering reliable and efficient solutions for businesses of all sizes.
                </Typography>
                
                {/* Social Media Icons */}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <IconButton 
                    sx={{ 
                      color: 'white', 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.3)' }
                    }}
                    component="a"
                    href="https://www.instagram.com/motextransport/"
                  >
                    <InstagramIcon />
                  </IconButton>
                </Stack>
              </Grid>
              
              {/* Quick Links - Horizontal on mobile, vertical on desktop */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontFamily: '"Poppins", sans-serif', fontWeight: 'bold', fontSize: '20px' }}>
                  Quick Links
                </Typography>
                <Box sx={{ display: { xs: 'grid', md: 'flex' }, gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'none' }, gap: { xs: 1, md: 0 }, flexDirection: { md: 'column' } }}>
                  <Link href="/" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Home
                  </Link>
                  <Link href="/about-us" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    About Us
                  </Link>
                  <Link href="#" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Services
                  </Link>
                  <Link href="/instant-quote" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Instant Quote
                  </Link>
                  <Link href="/gallery" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 1 } }}>
                    Gallery
                  </Link>
                  <Link href="#" color="inherit" underline="hover" sx={{ color: 'white', fontFamily: '"Poppins", sans-serif', fontWeight: 300, mb: { xs: 0, md: 0 } }}>
                    Contact
                  </Link>
                </Box>
              </Grid>
              
              {/* Contact Information */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, fontFamily: '"Poppins", sans-serif', fontWeight: 'bold', fontSize: '20px' }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ color: 'white', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                    Rozelle NSW 2039, Australia
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ color: 'white', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                    +61 423 440 056
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ color: 'white', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
                    roy@motextransport.com.au
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.3)', my: 4 }} />
            
            <Typography variant="body2" align="center" sx={{ color: 'white', opacity: 0.9, fontFamily: '"Poppins", sans-serif', fontWeight: 300 }}>
              © {new Date().getFullYear()} MOTEX Transport. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </ContentSection>

      {/* Mobile Menu */}
      <Drawer
        anchor="right"
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 300 },
            backgroundColor: DARKER_BG,
            padding: { xs: 2, sm: 3 }
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton 
            onClick={() => setIsMobileMenuOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box component="img" src="/MOTEX+Logo.png" alt="MOTEX Logo" sx={{ width: 120, my: 2 }} />
        
        <List>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Home" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/services');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' }
              }}
            >
              <ListItemText 
                primary="Services" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 600, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/about-us');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="About Us" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/instant-quote');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Instant Quote" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/gallery');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Gallery" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => {
                handleNavigation('/contact-us');
                setIsMobileMenuOpen(false);
              }}
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Contact Us" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          {/* Install the App option in hamburger menu */}
          <ListItem disablePadding>
            <ListItemButton 
              sx={{ 
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              <ListItemText 
                primary="Install the App" 
                primaryTypographyProps={{ 
                  fontFamily: '"Poppins", sans-serif', 
                  fontWeight: 400, 
                  color: 'white',
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          {/* App Download Options - Side by Side */}
          <Box sx={{ pl: 1.98, pr: 2, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => handleAppDownload('apple')}
              sx={{
                color: 'white',
                textTransform: 'none',
                py: 1,
                width: '48%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
                fontSize: '0.8rem',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '6px',
                '&:hover': { 
                  backgroundColor: 'rgba(222, 31, 39, 0.15)',
                  color: RED_COLOR 
                }
              }}
            >
              <AppleIcon sx={{ fontSize: '1.5rem', mb: 0.3 }} />
              Apple
            </Button>
            <Button
              onClick={() => handleAppDownload('android')}
              sx={{
                color: 'white',
                textTransform: 'none',
                py: 1,
                width: '48%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 500,
                fontSize: '0.8rem',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '6px',
                '&:hover': { 
                  backgroundColor: 'rgba(222, 31, 39, 0.15)',
                  color: RED_COLOR 
                }
              }}
            >
              <AndroidIcon sx={{ fontSize: '1.5rem', mb: 0.3 }} />
              Android
            </Button>
          </Box>

        </List>
        
        <Box sx={{ p: 2, mt: 2 }}>
          <Button 
            onClick={() => {
              handleNavigation('/instant-quote');
              setIsMobileMenuOpen(false);
            }}
            variant="contained" 
            fullWidth
            sx={{
              backgroundColor: RED_COLOR,
              color: 'white',
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 500,
              py: 1,
              borderRadius: 1,
              transition: '0.3s',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                backgroundColor: '#c50000',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            Get a Quote
          </Button>
        </Box>
      </Drawer>
    </PageWrapper>
    </PageTransition>
  );
};

export default ServicesPage;